import React from 'react';
import { TrendingUpIcon, TrendingDownIcon, TargetIcon, FlameIcon, TrophyIcon, ZapIcon, BarChart3Icon, CheckCircle2Icon } from 'lucide-react';
import { PeriodData, Timeframe, getPacingStatus, getPacingColor, getProjectedTotal, getAchievementStatus } from '../utils/goalData';
interface SidebarProps {
  periodData: PeriodData;
  timeframe: Timeframe;
}
export function Sidebar({
  periodData,
  timeframe
}: SidebarProps) {
  const spExpected = periodData.totalDays > 0 ? periodData.softPull.goal / periodData.totalDays * periodData.daysPassed : 0;
  const subExpected = periodData.totalDays > 0 ? periodData.submitted.goal / periodData.totalDays * periodData.daysPassed : 0;
  const spStatus = getPacingStatus(periodData.softPull.current, spExpected);
  const subStatus = getPacingStatus(periodData.submitted.current, subExpected);
  const spColors = getPacingColor(spStatus);
  const subColors = getPacingColor(subStatus);
  const spProjected = getProjectedTotal(periodData.softPull.current, periodData.daysPassed, periodData.totalDays);
  const subProjected = getProjectedTotal(periodData.submitted.current, periodData.daysPassed, periodData.totalDays);
  // Calculate streak from historical data
  const historicalAchievements = periodData.historicalPeriods.map(p => {
    const spPct = p.softPullGoal > 0 ? p.softPullActual / p.softPullGoal : 0;
    const subPct = p.submittedGoal > 0 ? p.submittedActual / p.submittedGoal : 0;
    return Math.round((spPct + subPct) / 2 * 100);
  });
  let streak = 0;
  for (const pct of historicalAchievements) {
    if (pct >= 100) {
      streak++;
    } else {
      break;
    }
  }
  const avgAchievement = Math.round(historicalAchievements.reduce((a, b) => a + b, 0) / historicalAchievements.length);
  const overallPct = Math.round((periodData.softPull.current / periodData.softPull.goal + periodData.submitted.current / periodData.submitted.goal) / 2 * 100);
  const elapsedPct = Math.round(periodData.daysPassed / periodData.totalDays * 100);
  return <aside className="w-72 flex-shrink-0 hidden lg:block" aria-label="Goal summary sidebar" data-id="element-204">
      <div className="sticky top-20 space-y-6" data-id="element-205">
        {/* Overall Progress */}

        {/* Metric Summaries */}

        {/* Stats */}
        <div className="bg-slate-800/80 rounded-2xl border border-slate-700/50 p-6 space-y-3" style={{
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
      }} data-id="element-206">
          <div className="flex items-center gap-2 mb-1" data-id="element-207">
            <TrophyIcon className="w-4 h-4 text-amber-400" data-id="element-208" />
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider" data-id="element-209">
              Stats
            </h3>
          </div>

          <div className="flex items-center justify-between py-2 border-b border-slate-700/50" data-id="element-210">
            <div className="flex items-center gap-2" data-id="element-211">
              <FlameIcon className="w-4 h-4 text-orange-400" data-id="element-212" />
              <span className="text-sm text-slate-400" data-id="element-213">Streak</span>
            </div>
            <span className="text-sm font-bold text-white" data-id="element-214">
              {streak} {timeframe === 'weekly' ? 'wk' : 'mo'}
            </span>
          </div>

          <div className="flex items-center justify-between py-2 border-b border-slate-700/50" data-id="element-215">
            <div className="flex items-center gap-2" data-id="element-216">
              {avgAchievement >= 100 ? <TrendingUpIcon className="w-4 h-4 text-emerald-400" data-id="element-217" /> : <TrendingDownIcon className="w-4 h-4 text-amber-400" data-id="element-218" />}
              <span className="text-sm text-slate-400" data-id="element-219">Avg Rate</span>
            </div>
            <span className={`text-sm font-bold ${avgAchievement >= 100 ? 'text-emerald-400' : 'text-slate-200'}`} data-id="element-220">
              {avgAchievement}%
            </span>
          </div>

          <div className="flex items-center justify-between py-2" data-id="element-221">
            <div className="flex items-center gap-2" data-id="element-222">
              <ZapIcon className="w-4 h-4 text-amber-400" data-id="element-223" />
              <span className="text-sm text-slate-400" data-id="element-224">Days Left</span>
            </div>
            <span className="text-sm font-bold text-white" data-id="element-225">
              {periodData.daysRemaining}
            </span>
          </div>
        </div>

        {/* Goal Achievers */}
        {periodData.loanOfficers.length > 0 && <div className="bg-slate-800/80 rounded-2xl border border-slate-700/50 p-6" style={{
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
      }} data-id="element-226">
            <div className="flex items-center gap-2 mb-4" data-id="element-227">
              <CheckCircle2Icon className="w-4 h-4 text-emerald-400" data-id="element-228" />
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider" data-id="element-229">
                Goal Achievers
              </h3>
            </div>

            <div className="space-y-2" data-id="element-230">
              {periodData.loanOfficers.map(officer => {
            const date = new Date(officer.completionDate);
            const formattedDate = date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            });
            return <div key={officer.id} className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0" data-id="element-231">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-232">
                      <CheckCircle2Icon className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" data-id="element-233" />
                      <span className="text-sm text-slate-200 truncate" data-id="element-234">
                        {officer.name}
                      </span>
                    </div>
                    <span className="text-xs text-slate-500 flex-shrink-0 ml-2" data-id="element-235">
                      {formattedDate}
                    </span>
                  </div>;
          })}
            </div>
          </div>}

        {/* Hot Streaks */}
        {periodData.streakUsers.length > 0 && <div className="bg-slate-800/80 rounded-2xl border border-slate-700/50 p-6" style={{
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
      }} data-id="element-236">
            <div className="flex items-center gap-2 mb-4" data-id="element-237">
              <FlameIcon className="w-4 h-4 text-orange-400" data-id="element-238" />
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider" data-id="element-239">
                Hot Streaks
              </h3>
            </div>

            <div className="space-y-2" data-id="element-240">
              {periodData.streakUsers.map(user => {
            return <div key={user.id} className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0" data-id="element-241">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-242">
                      <FlameIcon className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" data-id="element-243" />
                      <span className="text-sm text-slate-200 truncate" data-id="element-244">
                        {user.name}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-orange-400 flex-shrink-0 ml-2" data-id="element-245">
                      {user.streakCount} {timeframe === 'weekly' ? 'wk' : 'mo'}
                    </span>
                  </div>;
          })}
            </div>
          </div>}
      </div>
    </aside>;
}